import { PlanPeriodType } from "common/billing/util";
import { CHROME_EXTENSION_URL } from "common/extension/chrome";
import queryString from "query-string";

export type PathsPurchaseParams = {
  currency?: string;
  enterprise?: boolean;
  period?: PlanPeriodType;
  price?: number;
  repeat?: boolean;
  searchBulkId?: string;
  source?: string;
  trial?: boolean;
};

const paths = {
  apiDocs: (): string =>
    `${process.env.REACT_APP_HOST_WWW}/email-finder-api/docs`,
  authLogin: (query: { successUrl?: string } = {}): string =>
    queryString.stringifyUrl({
      query,
      url: "/auth/login",
    }),
  authPasswordRecover: (): string => "/auth/password",
  authSignup: (query: { successUrl?: string } = {}): string =>
    queryString.stringifyUrl({
      query,
      url: "/sign_up",
    }),
  chromeExtensionStore: () => CHROME_EXTENSION_URL,
  emailVerify: (): string => "/sign_up/verify/email/confirm",
  help: (): string => "https://help.anymailfinder.com/",
  integrations: (): string => "/integrations",
  invoiceFailedId: (
    id: string,
    query: { returnError?: boolean } = {},
  ): string =>
    queryString.stringifyUrl({
      query,
      url: `/invoice/${id}/failed`,
    }),
  legalApi: (): string =>
    `${process.env.REACT_APP_HOST_WWW}/legal#usage-of-the-api`,
  legalPrivacy: (): string => `${process.env.REACT_APP_HOST_WWW}/legal#privacy`,
  legalTerms: (): string => `${process.env.REACT_APP_HOST_WWW}/legal`,
  optOut: (): string => "/opt_out",
  payNow: (): string => "/settings/billing/pay_now",
  purchase: ({
    enterprise = false,
    period,
    source,
    trial = true,
  }: PathsPurchaseParams = {}): string =>
    queryString.stringifyUrl({
      query: {
        app: "1",
        enterprise: enterprise ? "1" : undefined,
        period,
        source,
        trial: trial ? undefined : "0",
      },
      url: `${process.env.REACT_APP_HOST_WWW}/pricing`,
    }),
  purchaseCheckout: (
    planId: string,
    {
      searchBulkId,
      trial = true,
      ...p
    }: PathsPurchaseParams & { returnError?: boolean } = {},
  ): string =>
    queryString.stringifyUrl({
      query: {
        ...p,
        search_bulk_id: searchBulkId,
        trial: trial ? undefined : "0",
      },
      url: `/purchase/checkout/${planId}`,
    }),
  purchaseReturnStripe: (
    planId: string,
    { currency, period, price, repeat, searchBulkId }: PathsPurchaseParams = {},
  ): string =>
    queryString.stringifyUrl({
      query: {
        currency,
        period,
        price,
        repeat,
        search_bulk_id: searchBulkId,
      },
      url: `/purchase/return/stripe/${planId}`,
    }),
  searchBulk: (
    searchId: string | undefined = undefined,
    download = false,
  ): string => {
    if (searchId && download) return `/search/bulk/${searchId}/download`;
    if (searchId) return `/search/bulk/${searchId}`;
    return "/search/bulk";
  },
  searchBulkMapColumns: (searchId: string): string =>
    `/search/bulk/${searchId}/map-columns`,
  searchBulkNew: (): string => "/search/bulk/new",
  searchSingle: (): string => "/search/single",
  settingsAccount: (): string => "/settings/account",
  settingsApi: (requestTrial = false): string =>
    `/settings/api${requestTrial ? "?request-trial=1" : ""}`,
  settingsAuth: (): string => "/settings/auth",
  settingsBilling: (): string => "/settings/billing",
  settingsBillingCancel: (): string => "/settings/billing/subscription/cancel",
  settingsBillingTrialConvert: (): string => "/settings/billing/trial-convert",
  settingsDelete: (): string => "/settings/delete-account",
  settingsIntegrations: (): string => "/settings/integrations",
  settingsTeam: (): string => "/settings/members/members",
  settingsUsage: (): string => "/settings/usage",
  status: (): string => "https://status.anymailfinder.com/",
  verify: (): string => "/verify",
  extractMap: (
    searchId: string | undefined = undefined,
    download = false,
  ): string => {
    if (searchId && download) return `/extract/map/${searchId}/download`;
    if (searchId) return `/extract/map/${searchId}`;
    return "/extract/map";
  },
  extractMapNew: (): string => "/extract/map/new",
  verifyBulkDownload: (id: string) => `/verify/bulk/${id}/download`,
};

export default paths;
